import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import amplitudeLog from '@client/amplitude';
import { AMP_ELEMENT_LIST_SEARCH } from '@shared/constants/amplitude-events';
import {
  useDebounce,
  useDebouncedFunction,
  usePrevious,
} from '@client/hooks/use-utils.hooks';
import { useElementSearchString } from '@client/hooks/use-filter-elements.hooks';

type UseElementListSearchFieldOptions = {
  /** Whether the current search query has any matching rows. Drives auto-close on click-outside. */
  hasMatches: boolean;
};

export function useElementListSearchField(
  { hasMatches }: UseElementListSearchFieldOptions = { hasMatches: true },
) {
  const [searchString, setSearchParamValue] = useElementSearchString();
  const [isSearchOpen, setIsSearchOpen] = useState(() => searchString !== '');
  const isSearchOpenRef = useRef(isSearchOpen);
  isSearchOpenRef.current = isSearchOpen;
  const debouncedSearchString = useDebounce(searchString, 1000);

  const [draft, setDraft] = useState(() => searchString);
  /** Bumped so {@link ElementListSearchField} can re-focus after Cmd+F. */
  const [focusRequestId, setFocusRequestId] = useState(0);
  const suppressDebouncedUrlRef = useRef(false);
  const searchInputFocusedRef = useRef(false);

  const applyDraftToUrl = useCallback(
    (next: string) => {
      if (suppressDebouncedUrlRef.current) return;
      setSearchParamValue(next);
    },
    [setSearchParamValue],
  );

  const debouncedApplyDraftToUrl = useDebouncedFunction(applyDraftToUrl, 100);

  const openSearch = useCallback(() => {
    suppressDebouncedUrlRef.current = false;
    // Fresh open clears the draft; reopen (e.g. Cmd+F) only re-focuses.
    if (!isSearchOpenRef.current) {
      setDraft('');
      setIsSearchOpen(true);
    }
    setFocusRequestId((id) => id + 1);
  }, []);

  const closeSearch = useCallback(() => {
    suppressDebouncedUrlRef.current = true;
    setDraft('');
    setSearchParamValue('');
    setIsSearchOpen(false);
  }, [setSearchParamValue]);

  const prevSearchString = usePrevious(searchString, { setInitial: true });
  if (searchString !== '' && !isSearchOpen) {
    setIsSearchOpen(true);
  }
  // External URL clear (e.g. "Go to element" navigation) → close the field so
  // we don't leave an open, unfocused search row behind. Skip when the input
  // is focused so user-driven clears keep the field open for further typing.
  if (
    searchString === '' &&
    prevSearchString !== '' &&
    !searchInputFocusedRef.current &&
    isSearchOpen
  ) {
    setIsSearchOpen(false);
    setDraft('');
  }

  useEffect(() => {
    if (searchInputFocusedRef.current) return;
    setDraft(searchString);
  }, [searchString]);

  useEffect(() => {
    if (debouncedSearchString.length > 0) {
      amplitudeLog(AMP_ELEMENT_LIST_SEARCH, { search: debouncedSearchString });
    }
  }, [debouncedSearchString]);

  /** While true, `searchString` changes from the URL do not overwrite `draft` (user is typing). */
  const setSearchInputFocused = useCallback((focused: boolean) => {
    searchInputFocusedRef.current = focused;
  }, []);

  /** Search closes via Escape, the close button, or — when conditions match — click-outside. */
  const handleDismiss = useCallback(() => {
    setSearchInputFocused(false);
    closeSearch();
  }, [closeSearch, setSearchInputFocused]);

  /**
   * Close on click-outside only when (a) the draft is empty, or (b) the current
   * query has no matches. Otherwise the field stays open so the user can keep
   * interacting with the filtered list.
   */
  const handleClickOutside = useCallback(() => {
    if (draft === '' || !hasMatches) closeSearch();
  }, [draft, hasMatches, closeSearch]);

  const onDraftChange = useCallback(
    (next: string) => {
      suppressDebouncedUrlRef.current = false;
      setDraft(next);
      debouncedApplyDraftToUrl(next);
    },
    [debouncedApplyDraftToUrl],
  );

  const fieldProps = useMemo(
    () => ({
      draft,
      onDraftChange,
      setSearchInputFocused,
      onDismiss: handleDismiss,
      onClickOutside: handleClickOutside,
      focusRequestId,
    }),
    [
      draft,
      onDraftChange,
      setSearchInputFocused,
      handleDismiss,
      handleClickOutside,
      focusRequestId,
    ],
  );

  return {
    isSearchOpen,
    openSearch,
    fieldProps,
  };
}
